/**
 * Connection Status Component
 * Displays MCP connection status and handles disconnect
 */
import React from 'react';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { 
  Loader2, 
  CheckCircle,
  XCircle,
  Link2,
  Server
} from 'lucide-react';

interface MCPConnectionStatus {
  connected: boolean;
  initialized: boolean;
  capabilities?: {
    tools?: string[];
    resources?: string[];
    prompts?: string[];
  };
  error?: string;
  lastConnected?: string;
}

interface MCPServerInfo {
  id: string;
  mcp_server_name: string;
  server_url: string;
  transport_type: string;
  metadata?: any;
  icon?: string;
}

interface ConnectionStatusProps {
  connectionStatus: MCPConnectionStatus | null;
  serverInfo: MCPServerInfo;
  loading: boolean;
  connecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onCancel: () => void;
}

export const ConnectionStatusDisplay: React.FC<ConnectionStatusProps> = ({
  connectionStatus,
  serverInfo,
  loading,
  connecting,
  onConnect,
  onDisconnect,
  onCancel
}) => {
  if (loading && !connectionStatus) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show connection status
  if (connectionStatus?.connected && connectionStatus?.initialized) {
    return (
      <div className="space-y-4">
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            {serverInfo.mcp_server_name} is connected via MCP protocol.
          </AlertDescription>
        </Alert>
        
        {/* Show transport type */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Server className="h-4 w-4" />
          <span>Transport: {serverInfo.transport_type}</span>
        </div>
        
        {/* Show capabilities if available */}
        {connectionStatus.capabilities && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Server Capabilities:</p>
            <div className="flex flex-wrap gap-2">
              {connectionStatus.capabilities.tools?.map(tool => (
                <Badge key={tool} variant="secondary">{tool}</Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Last connected time */}
        {connectionStatus.lastConnected && (
          <p className="text-xs text-muted-foreground">
            Last connected: {new Date(connectionStatus.lastConnected).toLocaleString()}
          </p>
        )}
        
        <div className="flex gap-2">
          <Button
            variant="destructive"
            onClick={onDisconnect}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Disconnect
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  // Show error state only for actual errors, not initial "not connected" state
  if (connectionStatus?.error && connectionStatus.error !== "Not connected") {
    return (
      <div className="space-y-4">
        <Alert className="border-red-500">
          <XCircle className="h-4 w-4 text-red-600" />
          <AlertDescription>
            Connection failed: {connectionStatus.error}
          </AlertDescription>
        </Alert>
        
        <div className="flex gap-2">
          <Button onClick={onConnect} disabled={connecting}>
            {connecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Connecting...
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Retry Connection
              </>
            )}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return null;
};